import React, {FC} from 'react';
import {useIntl} from 'react-intl';
import {ALIGN, Box, COLOR, Layout, SPACE, Text, useDevice, View} from '@lookiero/aurora';
import {Currency} from '@i18n';

import {Layout as LayoutUAF} from '@components/layouts/layout/Layout';
import {Section} from '@views/Referrals';
import {ShareButtons} from '../_components/ShareButtons/ShareButtons';
import {style} from './ReferralsSummary.styles';
import {ReferralsProvider, useReferrals} from '@views/Referrals/_contexts/ReferralsContext';
import {OverlaySpinner} from '@components/atoms';

const TEXT = {
  TITLE: {id: 'mgm.summary.title'},
  RECOMMENDED_FRIENDS_NUMBER: {id: 'mgm.summary.friends'},
  TOTAL_REFERRED_NUMBER: {id: 'mgm.summary.purchase'},
  SUMMARY_SUBTITLE: {id: 'raf.summary.list_header'},
};

const SummaryColumn: FC<{text: string; amount: number}> = ({amount, text}) => {
  const {screen} = useDevice();
  return (
    <View style={style.summaryColumn}>
      <Text heading={screen.L} level={screen.L ? 3 : 1} color={COLOR.TEXT_MEDIUM}>
        {amount}
      </Text>
      <Text detail level={screen.L ? 2 : 3} color={COLOR.TEXT_MEDIUM} marginTop={SPACE.SPACE_2}>
        {text}
      </Text>
    </View>
  );
};

const ReferredRow: FC<{name: string; amount: number}> = ({name, amount}) => {
  return (
    <View style={style.referredRow}>
      <Text level={3}>{name}</Text>
      <Text level={3}>
        +<Currency amount={amount} />
      </Text>
    </View>
  );
};

export const ReferralsSummary: FC = () => {
  return (
    <ReferralsProvider>
      <LayoutUAF style={{safeAreaView: style.fullContainer}}>
        <ReferralsSummaryView />
      </LayoutUAF>
    </ReferralsProvider>
  );
};

const ReferralsSummaryView: FC = () => {
  const {screen} = useDevice();
  const {formatMessage} = useIntl();
  const {
    loading,
    referralInfo: {
      numberOfReferredApplied,
      numberOfReferredRegistered,
      referreds,
      totalBalance,
      referralCode,
      referredCredit,
    },
  } = useReferrals();

  if (loading) return <OverlaySpinner />;

  return (
    <>
      <View style={style.outerContainer} testID={'outer'}>
        <Layout fullWidth={screen.L} style={style.containerStyle}>
          <Box
            size={{L: '1/2', M: '2/3'}}
            align={ALIGN.CENTER}
            marginVertical={screen.L ? SPACE.SPACE_16 : SPACE.SPACE_8}
          >
            <View>
              <Text heading level={screen.L ? 2 : 3}>
                {formatMessage(TEXT.TITLE)}
              </Text>
              <Text heading level={1} marginTop={SPACE.SPACE_2}>
                <Currency amount={totalBalance} />
              </Text>
            </View>
            <View marginTop={screen.L ? SPACE.SPACE_12 : SPACE.SPACE_8}>
              <ShareButtons
                referredCredit={referredCredit}
                section={Section.REFERRAL_VIEW}
                referralCode={referralCode}
              />
            </View>
            <View marginTop={screen.L ? SPACE.SPACE_16 : SPACE.SPACE_8} style={style.summary}>
              <SummaryColumn
                text={formatMessage(TEXT.RECOMMENDED_FRIENDS_NUMBER)}
                amount={numberOfReferredRegistered}
              />
              <View style={style.horizontalDivider} marginHorizontal={SPACE.SPACE_4} />
              <SummaryColumn text={formatMessage(TEXT.TOTAL_REFERRED_NUMBER)} amount={numberOfReferredApplied} />
            </View>
          </Box>
        </Layout>
      </View>
      <Layout fullWidth={screen.L} style={style.summaryDetailContainer}>
        <Box
          size={{L: '1/2', M: '2/3'}}
          align={ALIGN.CENTER}
          paddingVertical={screen.L ? SPACE.SPACE_16 : SPACE.SPACE_6}
        >
          <View style={style.summaryDetail} testID={'referrals-summary-view'}>
            <Text action level={3}>
              {formatMessage(TEXT.SUMMARY_SUBTITLE)}
            </Text>
            {referreds.map(({name, amount}, i) => (
              <ReferredRow name={name} amount={amount} key={i} />
            ))}
          </View>
        </Box>
      </Layout>
    </>
  );
};
